import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSplash } from '../../../contexts/SplashContext';
import TranslatableInput from '../../Common/TranslatableInput';
import TranslatableTextarea from '../../Common/TranslatableTextarea';
import LanguageSwitch from '../../Common/LanguageSwitch';
import CommonInput from '../../Common/CommonInput';
import CommonGroupInput from '../../Common/CommonGroupInput';
import CommonTextarea from '../../Common/CommonTextarea';
import CommonSelect from '../../Common/CommonSelect';
import CommonAsyncSelect from '../../Common/CommonAsyncSelect';
import CommonDate from '../../Common/CommonDate';
import CommonImageRadio from '../../Common/CommonImageRadio';
import CommonIconRadio from '../../Common/CommonIconRadio';
import CommonCheckbox from '../../Common/CommonCheckbox';
import ItemInput from '../../Common/ItemInput';

import { fetchSettings } from '../../../store/modules/cashmemodirect/cashmemodirectActions';
import useFormSettings from '../../../hooks/useFormSettings';

import { setCustomerSettings } from '../../../store/modules/customer/customerSlice';
import HandleQuickCreate from '../../Common/HandleQuickCreate';
import CustomerForm from '../Customers/CustomerForm';
import { createCustomer } from '../../../store/modules/customer/customerActions';

import { 
    calculateItemValues,
    calculateGrossTotal, 
    calculateOverallDiscount,
    distributeHeaderDiscount,
    calculateTotals
} from './CashMemoDirectCalculation';

import customerService from '../../../services/customerService';

import useModal from '../../../hooks/useModal';
import DataSelectionModal from '../../Common/CommonModal/DataSelectionModal';
import Items from '../../../pages/Items/Items'; // Adjust the path as needed
import Customers from '../../../pages/Customers/Customers';
import { useHasPermission } from '../../../utils/permissionUtils';

const CashMemoDirectForm = ({ initialData, onSave, isLoading, onClose, customActionsDispatch = {} }) => {

    const hasCreatePermission = useHasPermission(['create cash memo directs']);
    const hasUpdatePermission = useHasPermission(['update cash memo directs']);

    const { settings, isLoadingSettings, isSettingsInitialized } = useFormSettings('cashmemodirect', fetchSettings);
    const { countries, locations, selected } = settings || {};

    const splashData = useSplash();
    const languages = splashData?.languages || [];
    const defaultLanguage = languages.find(lang => lang.default)?.code || languages[0]?.code || 'en';
    const [selectedLanguage, setSelectedLanguage] = useState(defaultLanguage);

    const [typingTimer, setTypingTimer]         = useState(null);
    const doneTypingInterval = 800;

    const [formData, setFormData] = useState({
        id: '',
        invoice_number: '',
        invoice_date: '',
        customer: null,
        customer_name: {},
        phone_number: '',
        address1: {},
        address2: '',
        location: null,

        vat_number: '',
        vat_group: null,
        is_domestic: true,
        country: null,


        items: [],
        gross_total: '',
        discount_percentage: '',
        discount_amount: '',
        sub_total: '',
        vat_amount: '',

        net_total: '',
        advance_payment: '',
        payment: '',
        balance: '',
        payment_type: '',
        payment_date:  new Date().toISOString().split('T')[0],
        payment_amount: '',
        payment_reference: '',
        payments: []
    });
    
    const [errors, setErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);


    useEffect(() => {
        if (initialData) {
            setFormData({
                ...initialData,
                id: initialData.id || '',
                invoice_number: initialData.invoice_number || '',
                invoice_date: initialData.invoice_date || '',
                customer: initialData.customer || null,
                customer_name: initialData.customer_name || {},
                phone_number: initialData.phone_number || '',
                location: initialData.location || null,
                vat_number: initialData.vat_number || '',
                is_domestic: initialData.is_domestic || true,
                address1: initialData.address1 || {},
                address2: initialData.address2 || '',
                country: initialData.country || null,
                vat_group: initialData.vat_group || null,
                items: initialData.items || [],


                discount_percentage: initialData.discount_percentage || '',
                discount_amount: initialData.discount_amount || '',
                gross_total: initialData.gross_total || '',
                sub_total: initialData.sub_total || '',
                vat_amount: initialData.vat_amount || '',

                net_total: initialData.net_total || '',
                advance_payment: initialData.advance_payment || parseFloat(0).toFixed(3),
                balance: ((parseFloat(initialData.net_total) || 0) - (parseFloat(initialData.advance_payment) || 0)).toFixed(3),
                payment: parseFloat(0).toFixed(3),
                payment_type: initialData?.payment_type || '',
                payment_date: initialData?.payment_date || new Date().toISOString().split('T')[0],
                payment_amount: initialData?.payment_amount ? (parseFloat(initialData.payment_amount) || 0).toFixed(3) : '',
                payment_reference: initialData?.payment_reference || '',
                payments: initialData.payments ? initialData.payments.map(payment => ({
                    ...payment,
                    payment_amount: (parseFloat(payment.payment_amount) || 0).toFixed(3)
                })) : [],
            });
        }
    }, [initialData]);

    useEffect(() => {
        if (locations) {
            setFormData(prev => ({
                ...prev,
                location: prev.location || locations?.find(loc => loc.is_primary === 1) || null
            }));
        }
    }, [locations]);

    const calculateFormData = (formData) => {
        const grossTotal = calculateGrossTotal(formData.items);

        const overallDiscountAmount = calculateOverallDiscount(
            grossTotal,
            parseFloat(formData.discount_percentage) || 0,
            parseFloat(formData.discount_amount) || 0
        );
        const itemsWithHeaderDiscount = distributeHeaderDiscount(
            formData.items,
            overallDiscountAmount
        );
        const totals = calculateTotals(itemsWithHeaderDiscount);

        return {
            gross_total: grossTotal.toFixed(3),
            discount_amount: overallDiscountAmount.toFixed(3),
            sub_total: (grossTotal - overallDiscountAmount).toFixed(3),
            vat_amount: totals.vatAmount.toFixed(3),
            net_total: totals.netTotal.toFixed(3),
            balance: ((parseFloat(totals.netTotal) || 0) - (parseFloat(formData.advance_payment) || 0)).toFixed(3),
            items: itemsWithHeaderDiscount,

            payment_type: '', 
            payment_amount: '', 
            payment_date: new Date().toISOString().split('T')[0], 
            payment_reference: '',
            payments: []
        };
    }

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const itemFieldMatch = name.match(/^items\[(\d+)\]\.(.+)$/);

        setFormData(prev => {
            let newData = { ...prev };

            // Validate discount_percentage
            if (name === 'discount_percentage') {
                const numValue = parseFloat(value) || 0;
                if (numValue > 100) {
                    return prev; // Don't update if value exceeds 100
                }
            }

            if (itemFieldMatch) {
                const itemIndex = parseInt(itemFieldMatch[1]);
                const field = itemFieldMatch[2];
                
                const newItems = [...prev.items];
                const updatedItem = {
                    ...newItems[itemIndex],
                    [field]: type === 'checkbox' ? checked : value
                };

                // Only reset calculated values if the field is truly empty (not zero)
                if (['quantity', 'unit_price'].includes(field) && (value === '' || value === 0 || value === '0')) {
                    updatedItem.line_amount = '0.000';
                    updatedItem.vat_amount = '0.000';
                    updatedItem.header_discount = '0.000';
                    updatedItem.amount_excl_vat = '0.000';
                    updatedItem.amount_incl_vat = '0.000';
                } else if (['quantity', 'unit_price', 'discount_percentage'].includes(field)) {
                    const calculatedValues = calculateItemValues(updatedItem);
                    Object.assign(updatedItem, calculatedValues);
                }

                newItems[itemIndex] = updatedItem;
                newData.items = newItems;
            } else {
                newData[name] = type === 'checkbox' ? checked : value;
            }

            const calculatedData = calculateFormData(newData);
            return {
                ...newData,
                ...calculatedData
            };
        });
    };

    const handleDiscountChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => {
            return {
                ...prev,
                [name]: value
            }
        });

        if (value.length > 0) {
            clearTimeout(typingTimer);
            const timer = setTimeout(() => doneTyping(value, name), doneTypingInterval);
            setTypingTimer(timer);
        } else {
            doneTyping(value, name);
            clearTimeout(typingTimer);
        }
    }

    const doneTyping = async (value, name) => {
        setFormData(prev => {
            let newData = { ...prev };
            newData[name] = value;
            if(prev.gross_total*1 < value*1) {
                newData[name] = prev.gross_total;
            }

            const calculatedData = calculateFormData(newData);
            return {
                ...newData,
                ...calculatedData
            }; 
        });
    };

    

    const handleSelectChange = (name, value) => {
        if(name === 'customer') {
            
            setFormData(prev => {
                const isDomestic = value?.is_domestic !== undefined ? value.is_domestic : prev.is_domestic;

                // Update items with new VAT percentage based on is_domestic
                const updatedItems = prev.items.map(item => {
                    const vatPercentage = isDomestic 
                        ? item.vat_product_group?.vat_percentage || '0.000'
                        : '0.000';
                    
                    // Recalculate item values with new VAT percentage
                    const calculatedValues = calculateItemValues({
                        ...item,
                        vat_percentage: vatPercentage
                    });

                    return {
                        ...item,
                        vat_percentage: vatPercentage,
                        ...calculatedValues
                    };
                });
                
                const newData = {
                    ...prev,
                    [name]: value,
                    customer_name: value?.name || prev.customer_name,
                    phone_number: value?.phone || prev.phone_number,
                    vat_number: value?.vat_number || prev.vat_number,
                    is_domestic: (value?.is_domestic === 0) ? false : true || prev.is_domestic,
                    country: value?.country || prev.country,
                    vat_group: value?.vat_group ? {
                        id: value.vat_group.id,
                        label: value.vat_group.label,
                    } : prev.vat_group,
                    address1: value?.address1,
                    address2: value?.address2,
                    items: updatedItems,
                };

                const calculatedData = calculateFormData(newData);
                return {
                    ...newData,
                    ...calculatedData
                };
            });
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
        if (errors[name]) {
            setErrors(prev => ({...prev, [name]: ''}));
        }
    };

    const handleCustomerFormUpdate = (response, prev) => {
        const isDomestic = response?.is_domestic !== undefined ? response.is_domestic : prev.is_domestic;
        
        // Update items with new VAT percentage based on is_domestic
        const updatedItems = prev.items.map(item => {
            const vatPercentage = isDomestic 
                ? item.vat_product_group?.vat_percentage || '0.000'
                : '0.000';
            
            // Recalculate item values with new VAT percentage
            const calculatedValues = calculateItemValues({
                ...item,
                vat_percentage: vatPercentage
            });

            return {
                ...item,
                vat_percentage: vatPercentage,
                ...calculatedValues
            };
        });

        const newData = {
            ...prev,
            customer: {
                id: response.id,
                label: response.label
            },
            customer_name: response?.name || prev.customer_name,
            phone_number: response?.phone || prev.phone_number,
            vat_number: response?.vat_number || prev.vat_number,
            is_domestic: (response?.is_domestic === 0) ? false : true || prev.is_domestic,
            country: response?.country || prev.country,
            vat_group: response?.vat_group || prev.vat_group,
            address1: response?.address1,
            address2: response?.address2,
            items: updatedItems,
        };

        const calculatedData = calculateFormData(newData);
        return {
            ...newData,
            ...calculatedData
        };
    };

    const handleCustomerAddClick = HandleQuickCreate({
        title: 'Create Customer',
        FormComponent: CustomerForm,
        createAction: createCustomer,
        settingsKey: 'customers',
        formDataKey: 'customer',
        moduleName: 'customer',
        setFormData,
        setSettingsAction: setCustomerSettings,
        sidebarType: 'primary',
        className: 'right-sidebar-80',
        onQuickCreateDone: handleCustomerFormUpdate
    });

    const handlePaymentChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        // Auto-fill payment_amount with balance when payment type is selected
        if (name === 'payment_type' && value) {
            setFormData(prev => ({
                ...prev,
                [name]: value,
                payment_amount: parseFloat(prev.balance).toFixed(3) // Auto-fill with balance
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            }));
        }

        // Validate payment amount against balance when it changes
        if (name === 'payment_amount') {
            const currentBalance = parseFloat(formData.balance) || 0;
            const paymentAmount = parseFloat(value) || 0;
            if (paymentAmount > currentBalance) {
                setErrors(prev => ({
                    ...prev,
                    payment_amount: `Payment amount cannot exceed remaining balance of ${currentBalance.toFixed(3)}`
                }));
            } else if (errors.payment_amount) {
                setErrors(prev => ({...prev, payment_amount: ''}));
            }
        } else if (errors[name]) {
            setErrors(prev => ({...prev, [name]: ''}));
        }
    };
    
    const handleAddPayment = () => {
        // Validate payment type
        if (!formData.payment_type) {
            setErrors(prev => ({
                ...prev,
                payment_type: 'Please select a payment type'
            }));
            return;
        }

        // Validate payment amount
        if (!formData.payment_amount || parseFloat(formData.payment_amount) <= 0) {
            setErrors(prev => ({
                ...prev,
                payment_amount: 'Payment amount must be greater than 0'
            }));
            return;
        }

        // Validate payment amount against balance
        const currentBalance = parseFloat(formData.balance) || 0;
        const paymentAmount = parseFloat(formData.payment_amount) || 0;

        if (paymentAmount > currentBalance) {
            setErrors(prev => ({
                ...prev,
                payment_amount: `Payment amount cannot exceed remaining balance of ${currentBalance.toFixed(3)}`
            }));
            return;
        }

        setFormData(prev => ({
            ...prev,
            payment_type: '', 
            payment_amount: '', 
            payment_date: new Date().toISOString().split('T')[0], 
            payment_reference: '',
            payments: [...prev.payments, { 
                payment_type: prev.payment_type, 
                payment_amount: parseFloat(prev.payment_amount).toFixed(3), 
                payment_date: prev.payment_date, 
                payment_reference: prev.payment_reference 
            }]
        }));

        // Clear any existing errors after successful addition
        setErrors(prev => ({
            ...prev,
            payment_type: '',
            payment_amount: ''
        }));
    };

    const handleDeletePayment = (index) => {
        setFormData(prev => ({
            ...prev,
            payments: prev.payments.filter((_, i) => i !== index)
        }));
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setIsSaving(true);
            await onSave(formData);
            setErrors({});
            onClose();
        } catch (error) {
            if (error.errors) {
                const newErrors = {};
                Object.keys(error.errors).forEach(key => {
                    newErrors[key] = Array.isArray(error.errors[key]) 
                        ? error.errors[key][0] 
                        : error.errors[key];
                });
                setErrors(newErrors);
            } else {
                setErrors({
                    name: error.message || 'An error occurred while saving'
                });
            }
            return;
        } finally {
            setIsSaving(false);
        }
    };

    const isCreateMode = !formData?.id;
    const hasPermission = isCreateMode ? hasCreatePermission : hasUpdatePermission;

    const isSubmitting = isLoading || isSaving  || !hasPermission;
    const selectedLang = languages.find(l => l.code === selectedLanguage);


    const customerModal = useModal();
    const customerExternalCondition = {
        rules: {
            condition: 'and',
            children: [
                {
                    type: 'rule',
                    query: {
                        operator: 'equal',
                        rule: 'users.customer_type',
                        type: 'text',
                        value: '2'
                    }
                }
            ]
        }
    };

    const handleCustomerSelected = (customer) => {
        handleSelectChange('customer', customer);
        customerModal.hide();
    };


    const itemModal = useModal();
    const handleItemSelected = (selectedItems) => {
        // Get current items from form data
        const currentItems = formData.items || [];
        
        // Filter out items that already exist in the form
        const newItems = selectedItems.filter(selectedItem => 
            !currentItems.some(existingItem => existingItem.item?.id === selectedItem.id)
        );
        
        // Transform new items to match the form data structure
        const transformedItems = newItems.map(item => ({
                id: crypto.randomUUID(),
                item: {
                    id: item.id,
                    item_code: item.item_code,
                    label: item.name?.en || item.name?.ar || (typeof item.name === 'string' ? item.name : ''),
                },
                item_description: '',
                unit: item.measurement_unit?.label || '',
                quantity : '',
                unit_price: item.unit_price || '0.000',
                discount_percentage : '',
                line_amount : '',
                vat_amount : '',
                header_discount : '',
                amount_excl_vat : '',
                amount_incl_vat : '',
                vat_product_group: {
                    id: item.vat_group?.id,
                    label: item.vat_group?.label,
                    vat_percentage: item.vat_group?.vat_percentage || '0.000',
                }, // Convert object to string
                vat_percentage: (formData.is_domestic) ? item.vat_group?.vat_percentage || '0.000' : '0.000',
            }
        ));

        // Update form data with new items
        setFormData(prev => ({
            ...prev,
            items: [...prev.items, ...transformedItems]
        }));
    };


    const handleRemoveItem = (index) => {
        setFormData(prev => {
            const newItems = [...prev.items];
            newItems.splice(index, 1);
            
            const newData = {
                ...prev,
                items: newItems
            };

            const calculatedData = calculateFormData(newData);
            return {
                ...newData,
                ...calculatedData
            };
        });
    };


    // Calculate total payments and balance
    const calculatePaymentTotals = useCallback(() => {
        const totalPayments = formData.payments.reduce((sum, payment) => {
            return sum + (parseFloat(payment.payment_amount) || 0);
        }, 0);

        const netTotal = parseFloat(formData.net_total) || 0;
        const advancePayment = parseFloat(formData.advance_payment) || 0;
        const balance = netTotal - advancePayment - totalPayments;

        setFormData(prev => ({
            ...prev,
            payment: totalPayments.toFixed(3),
            balance: balance.toFixed(3)
        }));
    }, [formData.payments, formData.net_total, formData.advance_payment]);

    useEffect(() => {
        calculatePaymentTotals();
    }, [calculatePaymentTotals]);


    if (isLoadingSettings || !isSettingsInitialized) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    const handleExportPDF = () => {

        console.log('Export PDF');
    };

    return (
        <form onSubmit={handleSubmit} className="h-100 d-flex flex-column">
            <div className="d-flex justify-content-between mb-2">
                <div className="d-flex justify-content-start">
                    <h5 className="mb-0">Cash Memo Direct</h5>
                </div>
                <div className="d-flex justify-content-end">
                    {formData?.id && (
                        <a href={`${process.env.PUBLIC_URL}/cash_memo_directs/view/${formData.id}?preview=true`} target="_blank" className="btn btn-primary me-2" >
                            <i className="fas fa-print"></i> Print Preview
                        </a>
                    )} 
                    {/* <LanguageSwitch onLanguageChange={setSelectedLanguage} /> */}
                </div>
            </div>
            <div className="flex-grow-1">
                <div className="row">
                    <div className="col-md-4">
                        <CommonInput
                            name="invoice_number"
                            label="Invoice Number"
                            value={formData.invoice_number}
                            onChange={handleChange}
                            placeholder="Enter invoice number"
                            disabled={true}
                            errors={errors}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonDate
                            name="invoice_date"
                            label="Invoice Date"
                            value={formData.invoice_date}
                            onChange={handleChange}
                            placeholder="Enter invoice date"
                            disabled={isSubmitting}
                            errors={errors}
                            required={true}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-4">
                        <CommonAsyncSelect
                            name="customer"
                            label="Customer Code"
                            value={formData.customer}
                            onChange={(value) => handleSelectChange('customer', value)}
                            loadOptions={async (inputValue) => {
                                
                                const condition = {
                                    rules: {
                                        condition: 'and',
                                        children: [
                                            {
                                                type: 'rule',
                                                query: {
                                                    operator: 'equal',
                                                    rule: 'users.customer_type',
                                                    type: 'text',
                                                    value: '2'
                                                }
                                            }
                                        ]
                                    }
                                }

                                const response = await customerService.getList({ search: inputValue }, condition);
                                return response.data.map(item => ({
                                    id: item.id,
                                    name: item.name || '',
                                    label: item.name?.en+' ('+item.customer_code+')',
                                    country: item.country,
                                    phone: item.phone,
                                    vat_group: item.vat_group,
                                    vat_number: item.vat_number,
                                    is_domestic: item.is_domestic,
                                    address1: item.address1,
                                    address2: item.address2,
                                }));
                            }}
                            isSubmitting={isSubmitting}
                            errors={errors}
                            required={true}
                            actionButtons={[
                                {
                                    icon: 'fa fa-plus',
                                    label: '',
                                    className: 'btn-primary',
                                    onClick: (value) => handleCustomerAddClick()
                                },
                                {
                                    icon: 'fa fa-bars',
                                    label: '',
                                    className: 'btn-primary',
                                    onClick: (value) => customerModal.show()
                                }
                            ]}
                        />
                    </div>
                    <div className="col-md-4">
                        <TranslatableInput
                            label="Customer Name"
                            name="customer_name"
                            value={formData.customer_name}
                            onChange={handleChange}
                            errors={errors}
                            required={true}
                            disabled={isSubmitting}
                            selectedLanguage={selectedLanguage}
                            selectedLang={selectedLang}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonInput
                            name="phone_number"
                            label="Phone Number"
                            value={formData.phone_number}
                            onChange={handleChange}
                            placeholder="Enter phone number"
                            disabled={isSubmitting}
                            errors={errors}
                            required={true}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-4">
                        <TranslatableInput
                            label="Address 1"
                            name="address1"
                            value={formData.address1}
                            onChange={handleChange}
                            errors={errors}
                            required={false}
                            disabled={isSubmitting}
                            selectedLanguage={selectedLanguage}
                            selectedLang={selectedLang}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonInput
                            name="address2"
                            label="Address 2"
                            value={formData.address2}
                            onChange={handleChange}
                            placeholder="Enter address 2"
                            disabled={isSubmitting}
                            errors={errors}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonSelect
                            name="country"
                            label="Country"
                            value={formData.country}
                            onChange={(value) => handleSelectChange('country', value)}
                            options={countries}
                            isSubmitting={isSubmitting}
                            errors={errors}
                            required={true}
                            placeholder="Select country..."
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-4">
                        <CommonInput
                            name="vat_number"
                            label="VAT Number"
                            value={formData.vat_number}
                            onChange={handleChange}
                            placeholder="Enter vat number"
                            disabled={isSubmitting}
                            errors={errors}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonSelect
                            name="vat_group"
                            label="VAT Group"
                            value={formData.vat_group}
                            onChange={(value) => handleSelectChange('vat_group', value)}
                            options={[]}
                            isSubmitting={true}
                            errors={errors}
                            required={true}
                            placeholder="Select vat group..."
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonSelect
                            name="location"
                            label="Cust. Location"
                            value={formData.location}
                            onChange={(value) => handleSelectChange('location', value)}
                            options={locations}
                            isSubmitting={isSubmitting}
                            errors={errors}
                            required={true}
                            placeholder="Select location..."
                        />
                    </div>
                </div>


                <div className="row">
                    <div className="col-12 col-xl-12 mt-4">
                        {hasPermission && (
                            <button type="button" className="btn btn-primary" onClick={() => itemModal.show()}>
                                Add Item
                            </button>
                        )}

                        {errors?.items && (
                            <div className="text-danger mt-2">
                                {Array.isArray(errors['items']) ? errors['items'][0] : errors['items']}
                            </div>
                        )}

                        <div className="table-responsive mt-4">
                            <table className="table table-bordered">
                                <thead>
                                    <tr>
                                        <th className="text-nowrap p-3">Item Code</th>
                                        <th className="text-nowrap p-3">Item Name</th>
                                        <th className="text-nowrap p-3">Unit</th>
                                        <th className="text-nowrap p-3" style={{ minWidth: '150px' }}>Qty</th>
                                        <th className="text-nowrap p-3">Unit Price</th>
                                        <th className="text-nowrap p-3">Discount %</th>
                                        <th className="text-nowrap p-3">Line Amount</th>
                                        <th className="text-nowrap p-3">Header Discount</th>
                                        <th className="text-nowrap p-3">Amt Excl VAT</th>
                                        <th className="text-nowrap p-3">VAT Amount</th>
                                        <th className="text-nowrap p-3">Amt Incl VAT</th>
                                        <th className="text-nowrap p-3">Spl. Item Description</th>
                                        <th className="text-nowrap p-3">VAT Product Group</th>
                                        <th className="text-nowrap p-3" style={{ minWidth: '100px' }}>VAT %</th>
                                        <th className="text-nowrap p-3">Remove</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {formData.items && formData.items.length > 0 ? (
                                        formData.items.map((item, index) => (
                                            <tr key={index}>
                                                <td className="text-nowrap p-3 align-middle">{item.item?.item_code || 'N/A'}</td>
                                                <td className="text-nowrap p-3 align-middle">{item.item?.label || 'N/A'}</td>
                                                <td className="p-3 align-middle">{item.unit || 'N/A'}</td>
                                                <td className="p-3 align-middle">
                                                    <ItemInput
                                                        index={index}
                                                        field="quantity"
                                                        value={item.quantity}
                                                        onChange={handleChange}
                                                        disabled={isSubmitting}
                                                        errors={errors}
                                                        type="number"
                                                        required={true}
                                                        displayError={false}
                                                    />
                                                </td>
                                                <td className="p-3 align-middle">
                                                    <ItemInput
                                                        index={index}
                                                        field="unit_price"
                                                        value={item.unit_price}
                                                        onChange={handleChange}
                                                        disabled={isSubmitting}
                                                        errors={errors}
                                                        type="number"
                                                        required={true}
                                                        displayError={false}
                                                    />
                                                </td>
                                                <td className="p-3 align-middle">
                                                    <ItemInput
                                                        index={index}
                                                        field="discount_percentage"
                                                        value={item.discount_percentage}
                                                        onChange={handleChange}
                                                        disabled={isSubmitting}
                                                        errors={errors}
                                                        type="number"
                                                        displayError={false}
                                                    />
                                                </td>
                                                <td className="p-3 align-middle">{item.line_amount || '0.00'}</td>
                                                <td className="p-3 align-middle">{item.header_discount || '0.00'}</td>
                                                <td className="p-3 align-middle">{item.amount_excl_vat || '0.00'}</td>
                                                <td className="p-3 align-middle">{item.vat_amount || '0.00'}</td>
                                                <td className="p-3 align-middle">{item.amount_incl_vat || '0.00'}</td>
                                                <td className="p-3 align-middle">{item.item_description || 'N/A'}</td>
                                                <td className="p-3 align-middle">{item.vat_product_group?.label || 'N/A'}</td>
                                                <td className="p-3 align-middle">
                                                    <ItemInput
                                                        index={index}
                                                        field="vat_percentage"
                                                        value={item.vat_percentage}
                                                        onChange={handleChange}
                                                        disabled={isSubmitting}
                                                        errors={errors}
                                                        type="number"
                                                        required={true}
                                                        displayError={false}
                                                    />
                                                </td>
                                                <td className="p-3 align-middle">
                                                    <div>
                                                        <button 
                                                            type="button"
                                                            className="btn btn-danger btn-sm"
                                                            onClick={() => handleRemoveItem(index)}
                                                            disabled={isSubmitting}
                                                        >
                                                            <i className="fas fa-trash text-white"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="19" className="text-center">No items added</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className="row mt-4 d-flex justify-content-end">
                            <div className="col-md-4 order-md-2">
                                <div className="mb-2 row">
                                    <label className="col-lg-6 col-md-6 col-form-label">Gross Total:</label>
                                    <div className="col-lg-6 col-md-6">
                                        <CommonInput
                                            type="number"
                                            min={0}
                                            className="mb-0 custom-style text-end"
                                            name="gross_total"
                                            value={formData.gross_total}
                                            onChange={handleChange}
                                            disabled={true}
                                            errors={errors}
                                        />
                                    </div>
                                </div>
                                <div className="mb-2 row">
                                    <label className="col-lg-6 col-md-6 col-form-label">Discount %:</label>
                                    <div className="col-lg-6 col-md-6">
                                        <CommonInput
                                            type="number"
                                            className="mb-0 custom-style text-end"
                                            name="discount_percentage"
                                            value={formData.discount_percentage}
                                            onChange={handleChange}
                                            disabled={isSubmitting}
                                            errors={errors}
                                            min={0}
                                            max={100}
                                        />
                                    </div>
                                </div>
                                <div className="mb-2 row">
                                    <label className="col-lg-6 col-md-6 col-form-label">Discount Amt:</label>
                                    <div className="col-lg-6 col-md-6">
                                        <CommonInput
                                            type="number"
                                            min={0}
                                            className="mb-0 custom-style text-end"
                                            name="discount_amount"
                                            value={formData.discount_amount}
                                            onChange={handleDiscountChange}
                                            disabled={isSubmitting}
                                            errors={errors}
                                        />
                                    </div>
                                </div>
                                <div className="mb-2 row">
                                    <label className="col-lg-6 col-md-6 col-form-label">Sub Total:</label>
                                    <div className="col-lg-6 col-md-6">
                                        <CommonInput
                                            type="number"
                                            min={0}
                                            className="mb-0 custom-style text-end"
                                            name="sub_total"
                                            value={formData.sub_total}
                                            onChange={handleChange}
                                            disabled={true}
                                            errors={errors}
                                        />
                                    </div>
                                </div>
                                <div className="mb-2 row">
                                    <label className="col-lg-6 col-md-6 col-form-label">VAT Amt:</label>
                                    <div className="col-lg-6 col-md-6">
                                        <CommonInput
                                            type="number"
                                            min={0}
                                            className="mb-0 custom-style text-end"
                                            name="vat_amount"
                                            value={formData.vat_amount}
                                            onChange={handleChange}
                                            disabled={true}
                                            errors={errors}
                                        />
                                    </div>
                                </div>
                                <div className="mb-2 row">
                                    <label className="col-lg-6 col-md-6 col-form-label">Net Total:</label>
                                    <div className="col-lg-6 col-md-6">
                                        <CommonInput
                                            type="number"
                                            min={0}
                                            className="mb-0 custom-style text-end"
                                            name="net_total"
                                            value={formData.net_total}
                                            onChange={handleChange}
                                            disabled={true}
                                            errors={errors}
                                        />
                                    </div>
                                </div>
                                {!formData.is_locked && (
                                    <div className="mb-2 row">
                                        <label className="col-lg-6 col-md-6 col-form-label">Advance Payment:</label>
                                        <div className="col-lg-6 col-md-6">
                                            <CommonInput
                                                type="number"
                                                min={0}
                                                className="mb-0 custom-style text-end"
                                                name="advance_payment"
                                                value={formData.advance_payment}
                                                onChange={handleChange}
                                                disabled={true}
                                                errors={errors}
                                            />
                                        </div>
                                    </div>
                                )}
                                {hasPermission && (
                                    <div className="mb-2 row">
                                        <label className="col-lg-6 col-md-6 col-form-label">Balance:</label>
                                        <div className="col-lg-6 col-md-6">
                                            <CommonInput
                                                type="number"
                                                min={0}
                                                className="mb-0 custom-style text-end"
                                                name="balance"
                                                value={formData.balance}
                                                onChange={handleChange}
                                                disabled={true}
                                                errors={errors}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {hasPermission && (
                            <div className="col-12 col-md-6 order-md-1">
                                <div className="mb-2 row">
                                    <label className="col-lg-6 col-md-6 col-form-label">Payment:</label>
                                    <div className="col-lg-6 col-md-6">
                                        <CommonInput
                                            type="number"
                                            min={0}
                                            className="mb-0 custom-style text-end"
                                            name="payment"
                                            value={formData.payment}
                                            onChange={handleChange}
                                            disabled={true}
                                            errors={errors}
                                        />
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-12">
                                        <div className="table-responsive mt-4 mb-2">
                                            <table className="table table-bordered">
                                                <thead>
                                                    <tr>
                                                        <th>Type</th>
                                                        <th>Amount</th>
                                                        <th>Date</th>
                                                        <th>Reference</th>
                                                        <th>Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {formData?.payments && formData.payments.length > 0 ? (
                                                        formData.payments.map((payment, index) => (
                                                            <tr key={index}>
                                                                <td>{payment.payment_type}</td>
                                                                <td>{payment.payment_amount}</td>
                                                                <td>{payment.payment_date}</td>
                                                                <td>{payment.payment_reference}</td>
                                                                <td>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-danger btn-sm"
                                                                        onClick={() => handleDeletePayment(index)}
                                                                        disabled={isSubmitting}
                                                                    >
                                                                        <i className="fa fa-trash"></i>
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan="5" className="text-center">No payments added</td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                            {errors?.payments && (
                                                <div className="text-danger mt-2">
                                                    {Array.isArray(errors['payments']) ? errors['payments'][0] : errors['payments']}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className='row'>
                                    <div className='col-md-12'>
                                        <div className='row justify-content-center'>
                                            <div className='col col-sm-4 col-md-4 col-xxl-4 mb-4'>
                                                <CommonIconRadio
                                                    name="payment_type"
                                                    id="payment_type1"
                                                    value="cash"
                                                    label="Cash"
                                                    leftIcon="fa fa-money-bill"
                                                    onChange={handlePaymentChange}
                                                    checked={formData?.payment_type === 'cash'}
                                                    disabled={isSubmitting}
                                                    errors={errors}
                                                    tickContainerClassName="radio_tick_container"
                                                />
                                            </div>
                                            <div className='col col-sm-4 col-md-4 col-xxl-4 mb-4'>
                                                <CommonIconRadio
                                                    name="payment_type"
                                                    id="payment_type2"
                                                    value="bank"
                                                    label="Bank"  
                                                    leftIcon="fa fa-bank"
                                                    onChange={handlePaymentChange}
                                                    checked={formData?.payment_type === 'bank'}
                                                    disabled={isSubmitting}
                                                    errors={errors}
                                                    tickContainerClassName="radio_tick_container"
                                                />
                                            </div>
                                            <div className='col col-sm-4 col-md-4 col-xxl-4 mb-4'>
                                                <CommonIconRadio
                                                    name="payment_type"
                                                    id="payment_type3"
                                                    value="card"
                                                    label="Card"
                                                    leftIcon="fa fa-credit-card"
                                                    onChange={handlePaymentChange}
                                                    checked={formData?.payment_type === 'card'}
                                                    disabled={isSubmitting}
                                                    errors={errors}
                                                    tickContainerClassName="radio_tick_container"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <div className="mb-3">
                                            <label className="form-label">Payment Amount</label>
                                            <CommonInput
                                                type="number"
                                                name="payment_amount"
                                                onChange={handlePaymentChange}
                                                disabled={isSubmitting}
                                                value={formData.payment_amount}
                                                errors={errors}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="mb-3">
                                            <label className="form-label">Payment Date</label>
                                            <CommonDate
                                                type="date"
                                                name="payment_date"
                                                onChange={handlePaymentChange}
                                                disabled={isSubmitting}
                                                value={formData.payment_date}
                                                errors={errors}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-12">
                                        <div className="mb-3">
                                            <label className="form-label">Payment Reference</label>
                                            <CommonTextarea
                                                type="text"
                                                rows={2}
                                                name="payment_reference"
                                                onChange={handlePaymentChange}
                                                disabled={isSubmitting}
                                                value={formData.payment_reference}
                                                errors={errors}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-12">
                                        <button type="button" className="btn btn-warning" onClick={handleAddPayment}>Add Payment</button>
                                    </div>
                                </div>


                            </div>
                            )}

                        </div>
                    </div>
                </div>


                <DataSelectionModal
                    show={customerModal.isVisible}
                    onHide={customerModal.hide}
                    title=""
                    DataComponent={Customers}
                    selectionMode="single"
                    onDataSelected={handleCustomerSelected}
                    componentProps={{
                        // Any props specific to Suppliers component
                        excludedActions: ['create', 'actions', 'filter'],
                        externalCondition: customerExternalCondition
                    }}
                />

                <DataSelectionModal
                    show={itemModal.isVisible}
                    onHide={itemModal.hide}
                    title=""
                    DataComponent={Items}
                    selectionMode="multiple"
                    onRowsSelected={handleItemSelected}
                    selectionButtonText="Add Items"
                    selectionButtonIcon="fa fa-shopping-cart"
                    componentProps={{
                        excludedActions: ['create', 'actions', 'filter'],
                        isMultipleSelection: true,
                        // externalCondition: {
                        //     rules: {
                        //         condition: 'and',
                        //         children: [
                        //             {
                        //                 type: 'rule',
                        //                 query: {
                        //                     operator: 'equal',
                        //                     rule: 'items.item_type',
                        //                     type: 'text',
                        //                     value: 'ready_made'
                        //                 }
                        //             }
                        //         ]
                        //     }
                        // }
                    }}
                />
            </div>

            { hasPermission && (
            <div className="form-footer">
                <button 
                    type="submit" 
                    className="btn btn-primary" 
                    disabled={isSubmitting || !hasPermission}
                >
                           {isLoading ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Loading...
                        </>
                    ) : 
                    isSubmitting ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Saving...
                        </>
                    ) : 'Save'}
                </button>
            </div>
            )}
        </form>
    );
};

export default CashMemoDirectForm; 